<template>
    <div class="infinite-pagination-table" ref="infinitePaginationTable">
        <component-table
            ref="table"
            :items="realRenderedItems"
            :headers="headers"
            :provider="provider"
            :theme="theme"
            :search="search"
            :loading="loadingFirst"
            :sort-desc="sortDesc"
            @click:row="item => $emit('click:row', item)"
            @update:filtered="v => $emit('update:filtered', v)"
            @update:checked="v => $emit('update:checked', v)"
            @update:items="v => $emit('update:items', v)"
        >
            <!-- 부모의 slot 을 그대로 자식 컴포넌트로 전달합니다 -->
            <template
                v-for="(index, name) in $scopedSlots"
                v-slot:[name]="data"
            >
                <slot
                    :name="name"
                    v-bind="data"
                />
            </template>

            <!-- Infinite Pageination 을 위한 디텍터 설치 -->
            <template #body.append>
                <!--무한 스크롤 감지기-->
                <table-infinite-detector
                    v-if="!fullLoad"
                    v-show="!loadingFirst && !hidePagination"
                    ref="infiniteDetector"
                    :end-of-data="pager.endOfData"
                    :loading="loading"
                    @onNext="pager.next()"
                />
            </template>
        </component-table>
    </div>
</template>

<script>
    import InfinitePaginationTablePropsMixin
        from "@/assets/plugins/mps-form-table/component/infinite-pagination-component-table/mixins/InfinitePaginationTablePropsMixin";
    import ComponentTable from "@/assets/plugins/mps-form-table/component/component-table/ComponentTable";
    import TableInfiniteDetector
        from "@/assets/plugins/mps-form-table/component/infinite-pagination-component-table/pager/infinite-scroll/TableInfiniteDetector";
    import TablePager
        from "@/assets/plugins/mps-form-table/component/infinite-pagination-component-table/pager/paging/table-pager";

    /**
     * 무한 페이징을 구현하는 컴포넌트입니다.
     *
     * 이 컴포넌트에서는 오직 infinite scroll 만 구현합니다.
     */
    export default {
        name: "InfinitePaginationTable",
        components: {TableInfiniteDetector, ComponentTable},
        mixins: [InfinitePaginationTablePropsMixin],
        data() {
            return {
                pager: undefined,
                loadingNext: false,
                loadingFirst: false,
                realRenderedItems: [],
                counter: 0, // 초기 로딩시 테이블의 container clientHeight 에 아이템 목록이 다 찰 때까지 로딩하기 위한 카운터입니다.
            };
        },
        computed: {
            pageSize() {
                if (this.fullLoad) return 99999;
                if (typeof this.countPerPage !== 'number') return 99999;
                return this.countPerPage < 1 ? 99999 : this.countPerPage;
            },
        },
        watch: {
            counter() {
                if (!this.fullLoad && this.counter <= 5/* 최대 5번 까지 시도 */
                    && !this.pager.endOfData /* 데이터가 더 이상 없을 경우 종료 */
                    && this.isLackOfContent() /* 컨테이너보다 컨텐츠의 높이가 작아야 함 */) this.pager.next(); // 아이템 목록이 테이블을 채우지 못했다면 다시 로드합니다.
            },

            realRenderedItems() {
                this.$emit('update:items', this.realRenderedItems);
            },
        },
        created() {
            this.pager = new TablePager(this.page, this.pageSize, null);
            this.pager.onPageChangedListener = (page) => this.validate(page.pageNumber - 1, page.pageSize); // 첫 요청을 따로 하지 않기 때문에 처음 로드시 2가 들어옵니다. 그래서 -1 한 값을 넣어줍니다.
        },
        mounted() {
            this.loadingFirst = true;
            // 초기 mounted 에서 지연시간을 주어 페이지 로딩이 끝난 후 데이터를 요청합니다.
            setTimeout(() => {
                // mount 시 처음으로 아이템 목록을 요청합니다. container 안에 컨텐츠가 채워질 때까지 반복합니다. watch > counter 참고!
                this.pager.next();
                this.loadingFirst = false;
            }, 200);
            // 바텀은 absolute 로 고정되어 있기에 가로 스크롤시에도 고정이 되는 현상이 발생합니다.
            // 스크립트로 해당 현상을 해결합니다.
            this.$refs.infiniteDetector && this.$refs.infinitePaginationTable.querySelector('.form-table').addEventListener('scroll', e => {
                this.$refs.infiniteDetector.$el.style.left = e.currentTarget.scrollLeft + "px";
            });
        },
        methods: {
            /**
             * 아이템 목록이 컨테이너를 채웠는지 여부를 반환합니다.
             */
            isLackOfContent() {
                let trHeight = 0;
                document.querySelectorAll('.form-table__body > .form-table__body__tr').forEach(e => trHeight += e.clientHeight);
                return (document.querySelector('.form-table').clientHeight - document.querySelector('.form-table .form-table__header__tr').clientHeight)> (trHeight);
            },

            /**
             * pagenation 을 하기위한 next 메서드를 실행합니다.
             */
            validate(pageNumber, pageSize) {
                if (this.fullLoad && pageNumber > 1) return;
                const pageIndex = pageNumber - 1;
                const obj = {
                    startPage: pageIndex * pageSize,
                    endPage: pageNumber * pageSize,
                }
                let onNext = undefined; // 실제로 데이터를 로드하는 메서드입니다.
                // items 가 주어졌다면 내부적으로 아이템을 로드합니다.
                if (typeof this.next === 'function') onNext = this.next;

                if (!onNext && typeof onNext !== "function") throw new Error("items or next must be needed. cannot load more items!");

                this.loadingNext = true;
                Promise.resolve(onNext(pageNumber, pageSize, obj))
                    .then(async resItems => { // res 는 반드시 Array 여야 합니다.
                        try {
                            if (!Array.isArray(resItems)) throw new Error("next should return only Array");
                            const items = await this.pager.proceed(resItems);
                            this.counter++;
                            this.realRenderedItems = items;
                        } catch (e) {
                            console.error(e);
                            throw new Error("Unknown Error");
                        }
                        this.loadingNext = false;
                    })
                    .catch(e => {
                        console.error(e);
                        this.loadingNext = false;
                    });
            },

            /**
             * 렌더링된 아이템을 모두 제거하고 초기화합니다.
             */
            clearAndNext() {
                this.realRenderedItems.splice(0, this.realRenderedItems.length);
                this.pager.page = 1;
                this.$refs.table && this.$refs.table.clear();
                this.$refs.infiniteDetector && this.$refs.infiniteDetector.getContainer() && this.$refs.infiniteDetector.getContainer().scrollTo({left: 0, top: 0});
                this.counter = 0;
                this.loadingFirst = true;
                // 초기 mounted 에서 지연시간을 주어 페이지 로딩이 끝난 후 데이터를 요청합니다.
                setTimeout(() => {
                    // mount 시 처음으로 아이템 목록을 요청합니다. container 안에 컨텐츠가 채워질 때까지 반복합니다. watch > counter 참고!
                    this.pager.next();
                    this.loadingFirst = false;
                }, 200);
            },
        },
    }
</script>

<style scoped>
    .infinite-pagination-table {
        width: 100%;
        height: 100%;
    }
</style>